import { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';

import { Message } from '@components';
import { FormFieldset, TextField } from '@Forms/fields.jsx';
import { urlFormat } from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

export const TemplateForm = ({
  businessData,
  createSite,
  createTheme,
  onCancel,
  updateTheme,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    setFocus,
  } = useForm({
    defaultValues: {
      title: businessData.business_name + ' Template',
    },
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });

  const onSubmit = async (data) => {
    // create the blank theme
    const theme = await createTheme.mutateAsync({
      defer_jquery: true,
      display_name: data.title,
      jquery_asset: 'https://code.jquery.com/jquery-3.6.0.slim.min.js',
      render_mode: 'legacy',
    });
    // create site, assign the theme
    const site = await createSite.mutateAsync({
      business_id: businessData.id,
      base_directory: '/',
      formatted_domain: `template${theme.id}.example.com`,
      locations: [], // no location assignment
      theme_id: theme.id,
      title: data.title,
      type: 'template',
    });
    // update the theme with our default overrides
    //
    // id - passed so the mutation can identify the endpoint
    // site_override - setting the most common languages
    //   so we dont have to add later
    await updateTheme.mutateAsync({
      id: theme.id,
      site_override: {
        fr: site.id,
        en: site.id,
        es: site.id,
      },
      site_override_objects: [
        'asset_url_prefix',
        'domain_aliases',
        'extra_css',
        'footer_code',
        'header_code',
        'linklists',
        'pages',
        'posts',
        'plugins',
        'redirects',
      ],
    });
  };

  useEffect(() => {
    // set focus to the title field
    setFocus('title');
  }, [setFocus]);

  // combined statuses
  const isPending =
    createSite.isPending || createTheme.isPending || updateTheme.isPending;
  const isError =
    createSite.isError || createTheme.isError || updateTheme.isError;

  // form disabled status
  const disabled = isPending;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.title}
          options={{ required: 'This field is required' }}
          name="title"
          label="Template name"
          placeholder="Your Brand Local Template"
          helpText="Internal name for your template"
        />
      </FormFieldset>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          <button
            className="sb-btn-cancel sb-btn"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {isPending ? 'Creating...' : 'Create'}
            {isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
      </div>
    </form>
  );
};
